import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const AccessibilityContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const IssueItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.severity === 'error' ? '#dc3545' : props.severity === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const AccessibilityTest = () => {
  const [testResults, setTestResults] = useState({
    semantics: {
      headings: null,
      landmarks: null,
      lists: null,
      tables: null
    },
    aria: {
      roles: null,
      labels: null,
      states: null,
      properties: null
    },
    keyboard: {
      focusable: null,
      tabOrder: null,
      shortcuts: null
    },
    media: {
      altText: null,
      captions: null,
      transcripts: null
    }
  });

  const [issues, setIssues] = useState([]);

  const checkSemanticStructure = () => {
    const results = {
      headings: {
        total: document.querySelectorAll('h1, h2, h3, h4, h5, h6').length,
        proper: true
      },
      landmarks: {
        total: document.querySelectorAll('main, nav, header, footer, aside, section[role="region"]').length,
        proper: true
      },
      lists: {
        total: document.querySelectorAll('ul, ol').length,
        proper: document.querySelectorAll('ul:not(:empty), ol:not(:empty)').length
      },
      tables: {
        total: document.querySelectorAll('table').length,
        proper: document.querySelectorAll('table th[scope], table caption').length
      }
    };

    // Check heading hierarchy
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    let lastLevel = 0;
    results.headings.proper = headings.every(heading => {
      const level = parseInt(heading.tagName[1]);
      const isProper = level <= lastLevel + 1;
      lastLevel = level;
      return isProper;
    });

    setTestResults(prev => ({ ...prev, semantics: results }));

    const newIssues = [];
    if (!results.headings.proper) {
      newIssues.push({ severity: 'error', message: 'Improper heading hierarchy detected' });
    }
    if (results.lists.total !== results.lists.proper) {
      newIssues.push({ severity: 'error', message: 'Empty or improperly structured lists found' });
    }
    if (results.tables.total > 0 && results.tables.total !== results.tables.proper) {
      newIssues.push({ severity: 'error', message: 'Tables missing headers or captions' });
    }

    return newIssues;
  };

  const checkAriaImplementation = () => {
    const results = {
      roles: {
        total: document.querySelectorAll('[role]').length,
        valid: true
      },
      labels: {
        total: document.querySelectorAll('[aria-label], [aria-labelledby]').length,
        proper: document.querySelectorAll('button[aria-label], input[aria-label], img[aria-label]').length
      },
      states: {
        total: document.querySelectorAll('[aria-expanded], [aria-pressed], [aria-checked]').length,
        proper: true
      },
      properties: {
        total: document.querySelectorAll('[aria-describedby], [aria-controls]').length,
        proper: true
      }
    };

    setTestResults(prev => ({ ...prev, aria: results }));

    const newIssues = [];
    if (document.querySelectorAll('button:not([aria-label]):not(:has(*))').length > 0) {
      newIssues.push({ severity: 'error', message: 'Buttons without text or aria-label found' });
    }
    if (document.querySelectorAll('input:not([aria-label]):not([placeholder])').length > 0) {
      newIssues.push({ severity: 'error', message: 'Inputs without labels found' });
    }

    return newIssues;
  };

  const checkKeyboardAccessibility = () => {
    const results = {
      focusable: {
        total: document.querySelectorAll('a, button, input, select, textarea, [tabindex]').length,
        proper: document.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]').length
      },
      tabOrder: {
        total: document.querySelectorAll('[tabindex]').length,
        proper: document.querySelectorAll('[tabindex="-1"], [tabindex="0"]').length
      },
      shortcuts: {
        total: document.querySelectorAll('[accesskey]').length,
        proper: true
      }
    };

    setTestResults(prev => ({ ...prev, keyboard: results }));

    const newIssues = [];
    if (results.focusable.total !== results.focusable.proper) {
      newIssues.push({ severity: 'error', message: 'Some interactive elements are not keyboard accessible' });
    }
    if (results.tabOrder.total !== results.tabOrder.proper) {
      newIssues.push({ severity: 'warning', message: 'Custom tab order may disrupt keyboard navigation' });
    }

    return newIssues;
  };

  const checkMediaAccessibility = () => {
    const results = {
      altText: {
        total: document.querySelectorAll('img').length,
        proper: document.querySelectorAll('img[alt]').length
      },
      captions: {
        total: document.querySelectorAll('video').length,
        proper: document.querySelectorAll('video track[kind="captions"]').length
      },
      transcripts: {
        total: document.querySelectorAll('audio').length,
        proper: document.querySelectorAll('audio + [role="doc-subtitle"], audio + .transcript').length
      }
    };

    setTestResults(prev => ({ ...prev, media: results }));

    const newIssues = [];
    if (results.altText.total !== results.altText.proper) {
      newIssues.push({ 
        severity: 'error', 
        message: `${results.altText.total - results.altText.proper} images missing alt text` 
      });
    }
    if (results.captions.total !== results.captions.proper) {
      newIssues.push({ 
        severity: 'error', 
        message: `${results.captions.total - results.captions.proper} videos missing captions` 
      });
    }
    if (results.transcripts.total !== results.transcripts.proper) {
      newIssues.push({ 
        severity: 'error', 
        message: `${results.transcripts.total - results.transcripts.proper} audio elements missing transcripts` 
      });
    }

    return newIssues;
  };

  const runAllTests = () => {
    setIssues([]);
    
    const semanticIssues = checkSemanticStructure();
    const ariaIssues = checkAriaImplementation();
    const keyboardIssues = checkKeyboardAccessibility();
    const mediaIssues = checkMediaAccessibility();

    setIssues([
      ...semanticIssues,
      ...ariaIssues,
      ...keyboardIssues,
      ...mediaIssues
    ]);
  };

  const getStatusFromValue = (category, metric, value) => {
    if (!value) return 'error';
    
    switch (category) {
      case 'semantics':
      case 'aria':
        return value.proper ? 'good' : 'error';
      
      case 'keyboard':
        if (metric === 'focusable') {
          return value.total === value.proper ? 'good' : 'error';
        }
        return value.proper ? 'good' : 'warning';
      
      case 'media':
        return value.total === value.proper ? 'good' : 'error';
      
      default:
        return 'warning';
    }
  };

  return (
    <AccessibilityContainer>
      <TestSection>
        <h3>Accessibility Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Accessibility Tests
        </TestButton>

        <TestResults>
          <h3>Semantic Structure</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('semantics', 'headings', testResults.semantics.headings)}>
              <h4>Heading Hierarchy</h4>
              <p>
                {testResults.semantics.headings ? 
                  `${testResults.semantics.headings.total} headings, ${testResults.semantics.headings.proper ? 'proper' : 'improper'} structure` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('semantics', 'landmarks', testResults.semantics.landmarks)}>
              <h4>Landmarks</h4>
              <p>
                {testResults.semantics.landmarks ? 
                  `${testResults.semantics.landmarks.total} landmarks found` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('semantics', 'lists', testResults.semantics.lists)}>
              <h4>Lists</h4>
              <p>
                {testResults.semantics.lists ? 
                  `${testResults.semantics.lists.proper}/${testResults.semantics.lists.total} proper lists` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('semantics', 'tables', testResults.semantics.tables)}>
              <h4>Tables</h4>
              <p>
                {testResults.semantics.tables ? 
                  `${testResults.semantics.tables.proper}/${testResults.semantics.tables.total} proper tables` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>ARIA Implementation</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('aria', 'roles', testResults.aria.roles)}>
              <h4>ARIA Roles</h4>
              <p>
                {testResults.aria.roles ? 
                  `${testResults.aria.roles.total} roles, ${testResults.aria.roles.valid ? 'valid' : 'invalid'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('aria', 'labels', testResults.aria.labels)}>
              <h4>ARIA Labels</h4>
              <p>
                {testResults.aria.labels ? 
                  `${testResults.aria.labels.proper}/${testResults.aria.labels.total} proper labels` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('aria', 'states', testResults.aria.states)}>
              <h4>ARIA States</h4>
              <p>
                {testResults.aria.states ? 
                  `${testResults.aria.states.total} states used` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('aria', 'properties', testResults.aria.properties)}>
              <h4>ARIA Properties</h4>
              <p>
                {testResults.aria.properties ? 
                  `${testResults.aria.properties.total} properties used` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Keyboard Navigation</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('keyboard', 'focusable', testResults.keyboard.focusable)}>
              <h4>Focusable Elements</h4>
              <p>
                {testResults.keyboard.focusable ? 
                  `${testResults.keyboard.focusable.proper}/${testResults.keyboard.focusable.total} properly focusable` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('keyboard', 'tabOrder', testResults.keyboard.tabOrder)}>
              <h4>Tab Order</h4>
              <p>
                {testResults.keyboard.tabOrder ? 
                  `${testResults.keyboard.tabOrder.proper}/${testResults.keyboard.tabOrder.total} proper tab indices` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('keyboard', 'shortcuts', testResults.keyboard.shortcuts)}>
              <h4>Keyboard Shortcuts</h4>
              <p>
                {testResults.keyboard.shortcuts ? 
                  `${testResults.keyboard.shortcuts.total} shortcuts defined` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Media Accessibility</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('media', 'altText', testResults.media.altText)}>
              <h4>Image Alt Text</h4>
              <p>
                {testResults.media.altText ? 
                  `${testResults.media.altText.proper}/${testResults.media.altText.total} images with alt text` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('media', 'captions', testResults.media.captions)}>
              <h4>Video Captions</h4>
              <p>
                {testResults.media.captions ? 
                  `${testResults.media.captions.proper}/${testResults.media.captions.total} videos with captions` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('media', 'transcripts', testResults.media.transcripts)}>
              <h4>Audio Transcripts</h4>
              <p>
                {testResults.media.transcripts ? 
                  `${testResults.media.transcripts.proper}/${testResults.media.transcripts.total} audio with transcripts` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          {issues.length > 0 && (
            <>
              <h3>Accessibility Issues</h3>
              <IssueList>
                {issues.map((issue, index) => (
                  <IssueItem key={index} severity={issue.severity}>
                    {issue.message}
                  </IssueItem>
                ))}
              </IssueList>
            </>
          )}
        </TestResults>
      </TestSection>
    </AccessibilityContainer>
  );
};

export default AccessibilityTest; 